import React from 'react';
import ReactModal from 'react-modal';
import IconClose from '../../images/icon-close.svg';
import { GotradeConstant } from '../../utils/constant';
import TrophyIcon from '../../images/id-options-trading/trophy.png';
import ModalBackground from '../../images/gti/download/modal-bg.png';
import downloadAppstore from '../../images/download-appstore-badge.svg';
import downloadPlaystore from '../../images/download-play-badge.svg';
import mockupTwoPhone from '../../images/mockup-two-phone.png';
import AppsPerformance from './shared/apps-performance';
import QRCode from 'react-qr-code';
import AdjustTrackedLink from '../adjust-tracked-link';
import AdjustDownloadQrCode from '../adjust-download-qr-code';

const AppDownloadModal = ({ isOpen, onClose, downloadLink = null, qrLink = null, appKind = 'gti' }) => {
  // SEOENG-117: the QR encodes the CTA's own link when it has one, else the page-tagged QR tracker
  // link; the static image stays only as the fallback when neither exists.
  // SEOENG-125: on a GTG page the modal is the global app's: GTG store fallbacks and app kind, the
  // global app's store ratings, and no Indonesia or JFX claims (Labuan reverse solicitation). The
  // GTI render is unchanged. The GTI fallback QR is the live GTI tracker (SEOENG-186), so GTG never
  // falls back to it.
  const isGtg = appKind === 'gtg';
  const qrValue = downloadLink || qrLink || (isGtg ? GotradeConstant.APPSTORE_LINK : null);
  const appStoreFallback = isGtg ? GotradeConstant.APPSTORE_LINK : GotradeConstant.GTI_APPSTORE_LINK;
  const playStoreFallback = isGtg
    ? GotradeConstant.PLAYSTORE_LINK
    : GotradeConstant.GTI_PLAYSTORE_LINK;

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={{
        content: {
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          position: 'fixed',
          maxWidth: 1024
        }
      }}
      className="modal relative z-50 h-screen w-screen overflow-hidden outline-none lg:h-auto lg:w-3/4"
      overlayClassName="fixed inset-0 bg-gray-900 bg-opacity-25 z-50"
    >
      <div
        className="flex h-full flex-col bg-white bg-cover bg-center bg-no-repeat md:block md:h-auto"
        style={{
          backgroundImage: `url(${ModalBackground})`
        }}
      >
        <div
          className="close-button cursor-pointer"
          onClick={onClose}
          style={{ position: 'absolute', top: 40, right: 30 }}
        >
          <img className="h-4 w-4 lg:h-5 lg:w-5" src={IconClose} />
        </div>
        <div
          className="flex flex-1 flex-col overflow-hidden pt-16 md:block"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          <div className="flex flex-1 flex-col px-5 md:block md:px-28">
            <div>
              {isGtg ? (
                // The global app's store listings, same figures as the GTG home.
                <AppsPerformance
                  appStoreRating="4.7"
                  appStoreReviews="370"
                  playStoreRating="4.7"
                  playStoreReviews="13.5K"
                />
              ) : (
                <AppsPerformance></AppsPerformance>
              )}
            </div>
            <div className="my-4">
              <h2 className="central-no1-extra text-center text-3xl font-black uppercase leading-[90%] md:text-[64px]">
                start INVESTING in<br></br>
                <span className="text-gotrade-green">us stocks</span> IN SECONDS
              </h2>
              <p className="mt-4 text-center text-base md:text-[20px]">
                Own a slice of Tesla, Nvidia, Google, or Apple. For less than your morning coffee.
                <br></br> Start investing with just $1!
              </p>
              <p className="mt-4 text-center text-base font-medium md:text-xl">
                Tap the button below, or scan the QR
              </p>
            </div>

            {/* SEOENG-201: a flex column does not collapse margins, so on phones this row's top margin is
                mt-4 (with the copy's mb-4 that is the same 32 px as before); md:my-8 keeps desktop. */}
            <div className="mb-8 mt-4 flex w-full gap-4 md:my-8">
              <div className="grid w-1/2 grid-cols-1 justify-items-end">
                <AdjustTrackedLink
                  href={downloadLink ?? appStoreFallback}
                  appKind={isGtg ? 'gtg' : 'gti'}
                  target="_blank"
                >
                  <img src={downloadAppstore} className="mb-2 w-32" alt="Download App Store"></img>
                </AdjustTrackedLink>
                <AdjustTrackedLink
                  href={downloadLink ?? playStoreFallback}
                  appKind={isGtg ? 'gtg' : 'gti'}
                  target="_blank"
                >
                  <img
                    src={downloadPlaystore}
                    className="flex-none mb-2 w-32"
                    alt="Download Play Store"
                  ></img>
                </AdjustTrackedLink>
              </div>
              <div>
                {qrValue ? (
                  <QRCode
                    className="flex-none app-download-qrcode mx-auto w-[90px]"
                    value={qrValue}
                    size={90}
                  />
                ) : (
                  // SEOENG-186: a modal opened with no QR link (What's New v3) shows the live QR
                  // tracker with a creative, never the static image that carried none.
                  <AdjustDownloadQrCode
                    className="flex-none app-download-qrcode mx-auto w-[90px]"
                    size={90}
                  />
                )}
              </div>
            </div>
            {/* SEOENG-201: on phones the two stat blocks share the first line, 24 px apart, and the mockup
                takes its own line under them (it got about 30 px between them). The row fills the rest of
                the modal and content-between keeps the stats 32 px under the badges (the row above's mb-8)
                while the phones rise from the modal's bottom edge, so spare height on tall screens sits
                between the two. From md up the layout is as before. */}
            <div className="flex flex-1 flex-wrap content-between items-center justify-between gap-x-6 md:mt-8 md:flex-nowrap md:gap-x-0">
              {isGtg ? (
                <div className="w-40 max-w-[calc(50%-0.75rem)] text-center md:max-w-none">
                  <h3 className="central-no1-extra text-[40px] font-black text-gotrade-green">$1</h3>
                  <h3 className="text-sm font-normal text-n-800">
                    is all it takes to own a slice of a US stock
                  </h3>
                </div>
              ) : (
                <div className="w-40 max-w-[calc(50%-0.75rem)] text-center md:max-w-none">
                  <img src={TrophyIcon} className="mx-auto w-[70px]"></img>
                  <h3 className="mt-2 text-sm">#1 US Stock Platform in Indonesia by</h3>
                  <h4 className="text-sm font-medium">JFX PALN 2024 Awards</h4>
                </div>
              )}
              {/* SEOENG-201: on phones the two phones span the row's full width and sit centred on it. The
                  file's phones are opaque at x 482 to 1456 of 1834 (the rest is a soft shadow), so the image
                  is 1834/975 = 188.1% of the row wide and starts 482/975 = 49.44% of the row to its left;
                  the overflow-hidden column above clips the shadow at the screen's edges. From md up the image
                  is sized as before. */}
              <div className="order-last basis-full md:order-none md:flex-1">
                <img
                  src={mockupTwoPhone}
                  alt="Mockup Two Phones"
                  className="-mb-[5px] -ml-[49.44%] h-auto w-[188.1%] max-w-none md:ml-0 md:w-auto md:max-w-full"
                ></img>
              </div>
              {isGtg ? (
                <div className="w-40 max-w-[calc(50%-0.75rem)] text-center md:max-w-none">
                  <h3 className="central-no1-extra text-[40px] font-black text-gotrade-green">1M+</h3>
                  <h3 className="text-sm font-normal text-n-800">
                    people have signed up to Gotrade Global
                  </h3>
                </div>
              ) : (
                <div className="w-40 max-w-[calc(50%-0.75rem)] text-center md:max-w-none">
                  {/* SEOENG-138 (PMC-175): Maria's cleared wording; GTI registered users are ~844K. */}
                  <h3 className="central-no1-extra text-[40px] font-black text-gotrade-green">800K+</h3>
                  <h3 className="text-sm font-normal text-n-800">
                    people in Indonesia have signed up to Gotrade
                  </h3>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </ReactModal>
  );
};

export default AppDownloadModal;
